import React, { useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import "./UserTag.css";
import { USER_TAG } from "../../../shared/constants/valisdation";
import { searchContentCreators } from "../../../shared/actions/contentAction";
import LaxyImage from "../../../components/LazyImage";

/**
 * UserTag Component
 * 
 * A reusable component for tagging users with search functionality
 * 
 * @param {boolean} isOpen - Controls visibility of the tag popup
 * @param {Array} initialSelectedUsers - Array of initially selected users
 * @param {Function} onConfirm - Callback function called with selected users array when confirmed
 * @param {Function} onClose - Callback function called when popup is closed
 */
const UserTag = ({
  isOpen = false,
  initialSelectedUsers = [],
  onConfirm,
  onClose
}) => {
  // State management
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [selectedTaggedUsers, setSelectedTaggedUsers] = useState(initialSelectedUsers);
  const [tagSearchLoading, setTagSearchLoading] = useState(false);

  const searchTimeout = useRef(null);

  // Search for users
  const searchUsers = useCallback(async (query) => {
    if (!query || query.trim().length < USER_TAG.MIN_SEARCH_LENGTH) {
      setTagSearchResults([]);
      return;
    }
    setTagSearchLoading(true);

    try {
      const result = await searchContentCreators(query, {
        limit: 10,
        skip: 0,
        sortBy: 'username_asc'
      });

      if (result.success && result.data) {
        // Check if result.data is an array or has a nested data array
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data.data && Array.isArray(result.data.data))
            ? result.data.data
            : [];

        if (dataArray.length > 0) {
          // Users are already processed with signed URLs in the service
          setTagSearchResults(dataArray);
        } else {
          setTagSearchResults([]);
        }
      }
    } catch (error) {
      setTagSearchResults([]);
    } finally {
      setTagSearchLoading(false);
    }
  }, []);

  // Handle tag search input change
  const handleTagSearchChange = useCallback((e) => {
    const query = e.target.value;
    setTagSearchQuery(query);

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchUsers(query);
    }, USER_TAG.SEARCH_DEBOUNCE_MS);
  }, [searchUsers]);

  // Handle user selection for tagging
  const handleUserSelect = useCallback((user) => {
    setSelectedTaggedUsers(prev => {
      const isAlreadySelected = prev.some(selected => selected.username === user.username);
      return isAlreadySelected ? prev : [...prev, user];
    });
  }, []);

  // Handle user removal from tagged users
  const handleUserRemove = useCallback((username) => {
    setSelectedTaggedUsers(prev =>
      Array.isArray(prev) ? prev.filter(user => user && user.username !== username) : []
    );
  }, []);

  // Handle tag popup close
  const handleClose = useCallback(() => {
    setTagSearchQuery("");
    setTagSearchResults([]);
    setSelectedTaggedUsers(initialSelectedUsers);
    if (onClose) {
      onClose();
    }
  }, [initialSelectedUsers, onClose]);

  // Confirm and persist tag selection
  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(selectedTaggedUsers);
    }
    setTagSearchQuery("");
    setTagSearchResults([]);
  }, [selectedTaggedUsers, onConfirm]);

  // Reset selected users when initialSelectedUsers changes
  React.useEffect(() => {
    setSelectedTaggedUsers(initialSelectedUsers);
  }, [initialSelectedUsers]);

  if (!isOpen) {
    return null;
  }


  return ReactDOM.createPortal(
    <div className="tag-popup-overlay cm_bg_blur" onClick={handleClose}>
      <div className="tag-popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="tag-popup-header">
          <h3 className="tag-popup-title">Tag Users</h3>
          <button
            type="button"
            className="tag-popup-close"
            onClick={handleClose}
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="tag-popup-search">
          <div className="tag-popup-search-container">
            <div className="tag-popup-search-icon">
              <i className="fa-solid fa-search"></i>
            </div>
            <input
              type="text"
              placeholder="Search users..."
              className="tag-popup-search-input"
              value={tagSearchQuery}
              onChange={handleTagSearchChange}
            />
            {tagSearchLoading && (
              <div className="tag-popup-search-loading">
                <i className="fa fa-spinner fa-spin"></i>
              </div>
            )}
          </div>
        </div>

        {selectedTaggedUsers.length > 0 && (
          <div className="tag-popup-selected">
            <div className="tag-popup-selected-container">
              {selectedTaggedUsers.map((user, index) => {
                // Use username as the primary key since it's unique and stable
                // Fallback to array index only if no stable identifier exists
                const uniqueKey = user.username || user._id || user.id || `user-${index}`;
                return (
                  <div key={uniqueKey} className="tag-popup-selected-user">
                    <LaxyImage
                      src={user.profile_picture || USER_TAG.DEFAULT_AVATAR}
                      alt="User"
                      className="tag-popup-selected-avatar"
                    />
                    <span className="tag-popup-selected-username">@{user.username}</span>
                    <button
                      type="button"
                      className="tag-popup-selected-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserRemove(user.username);
                      }}
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="tag-popup-user-list">
          {tagSearchResults.length > 0 ? (
            tagSearchResults.map((user) => {
              const isSelected = selectedTaggedUsers.some(selected => selected.username === user.username);
              // Use username as the primary key since it's unique and stable
              const uniqueKey = user.username || user._id || user.id;
              return (
                <div
                  key={uniqueKey}
                  className="tag-popup-user-item"
                  onClick={() => handleUserSelect(user)}
                >
                  <LaxyImage
                    src={user.profile_picture || USER_TAG.DEFAULT_AVATAR}
                    alt="User"
                    className="tag-popup-user-avatar"
                  />
                  <div className="tag-popup-user-info">
                    <div className="tag-popup-user-username">@{user.username}</div>
                  </div>
                  {isSelected ?
                    <div className={`tag-popup-user-checkbox checked`}>
                      <i className="fa fa-check"></i>
                    </div>
                    :
                    <span className="tag-popup-user-checkbox">
                    </span>
                  }
                </div>
              );
            })
          ) : tagSearchQuery.length >= USER_TAG.MIN_SEARCH_LENGTH && !tagSearchLoading ? (
            <div className="tag-popup-no-results">
              <p>No users found</p>
            </div>
          ) : tagSearchQuery.length < USER_TAG.MIN_SEARCH_LENGTH ? (
            <div className="tag-popup-no-results">
              <p>Type at least {USER_TAG.MIN_SEARCH_LENGTH} characters to search</p>
            </div>
          ) : null}
        </div>

        <div className="tag-popup-footer">
          <button
            type="button"
            className="tag-popup-cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tag-popup-confirm"
            onClick={handleConfirm}
          >
            Tag ({selectedTaggedUsers.length})
          </button>
        </div>
      </div>
    </div>,
    document.body
  )

};

export default UserTag;

